import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
} from "react";
import { axiosInstance } from "config/api";
import { fetchDoctorsByClinic, fetchAllDoctors } from "services/doctorService";

/**
 * GlobalFiltersContext
 * Central source of truth for clinic, doctor, date range selections across
 * Consultations / Appointments / Patient List.
 *
 * Exposed shape:
 * {
 *   filters: { clinicId, doctorId, doctorIdsEffective, fromDate, toDate, roleContext },
 *   loading: { clinics: boolean, doctors: boolean },
 *   lists: { clinics: Clinic[], doctors: Doctor[] },
 *   actions: { setClinicId, setDoctorId, setFromDate, setToDate, forceReloadDoctors }
 * }
 *
 * Role logic handled here so UI consumers can stay simple.
 */
const GlobalFiltersContext = createContext(null);
export const useGlobalFilters = () => useContext(GlobalFiltersContext);

// Utility: resolve user role from multiple possible storage keys
function resolveUserRole() {
  let rawUser, tokenPayload, doctorData;
  try {
    rawUser = JSON.parse(localStorage.getItem("user") || "null");
  } catch {
    rawUser = null;
  }
  try {
    tokenPayload = JSON.parse(localStorage.getItem("tokenPayload") || "null");
  } catch {
    tokenPayload = null;
  }
  try {
    doctorData = JSON.parse(localStorage.getItem("doctorData") || "null");
  } catch {
    doctorData = null;
  }

  // Normalize candidate role strings safely
  const roleRaw =
    rawUser?.role || tokenPayload?.role || rawUser?.role_in_clinic || "" || "";
  const role = String(roleRaw).toLowerCase().trim();
  const ric = String(rawUser?.ric || tokenPayload?.ric || "").toLowerCase();
  const roleInClinic = String(rawUser?.role_in_clinic || "").toLowerCase();

  const isOwner = ric === "owner" || roleInClinic === "owner";
  const isAdmin =
    role === "admin" || roleInClinic === "admin" || rawUser?.isAdmin === true;
  const isDoctor = role === "doctor" || (!role && !isAdmin && !isOwner); // fallback doctor

  // Improved front-desk detection: accept multiple common variants (front_desk, frontdesk, front desk, front-desk)
  const isFrontdesk = [
    "front_desk",
    "front-desk",
    "frontdesk",
    "front desk",
    "front",
  ].some((k) => role.includes(k));

  const userDoctorId =
    tokenPayload?.userId ||
    tokenPayload?.doctor_id ||
    tokenPayload?.id ||
    doctorData?.id ||
    rawUser?.doctor_id ||
    null;
  const userClinicId =
    doctorData?.clinic_id ||
    rawUser?.clinic_id ||
    tokenPayload?.clinic_id ||
    null;

  return {
    isOwner,
    isAdmin,
    isDoctor,
    isFrontdesk,
    userDoctorId,
    userClinicId,
  };
}

export function GlobalFiltersProvider({ children, initial = {} }) {
  const roleMeta = resolveUserRole();
  const {
    isAdmin,
    isOwner,
    isDoctor,
    isFrontdesk,
    userDoctorId,
    userClinicId,
  } = roleMeta;

  // State
  const [clinics, setClinics] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [clinicId, setClinicId] = useState(
    initial.clinicId ?? (isAdmin ? null : userClinicId || null),
  );
  const [doctorId, setDoctorId] = useState(
    initial.doctorId ?? (isDoctor ? String(userDoctorId) : ""), // blank means aggregate for non-exclusive roles
  );
  const today = new Date().toISOString().split("T")[0];
  const [fromDate, setFromDate] = useState(initial.fromDate || today);
  const [toDate, setToDate] = useState(initial.toDate || today);

  const [loadingClinics, setLoadingClinics] = useState(false);
  const [loadingDoctors, setLoadingDoctors] = useState(false);

  // Request token to avoid race conditions
  const doctorsReqTokenRef = useRef(0);

  // Fetch clinics when needed
  useEffect(() => {
    if (isAdmin || isOwner) {
      setLoadingClinics(true);
      axiosInstance
        .get("/api/clinic/allClinics")
        .then((r) => {
          const list = Array.isArray(r.data)
            ? r.data
            : r.data.clinics || r.data.data || [];
          setClinics(list);
        })
        .catch(() => setClinics([]))
        .finally(() => setLoadingClinics(false));
    }
  }, [isAdmin, isOwner]);

  const loadDoctors = useCallback(
    async (targetClinicId) => {
      const token = ++doctorsReqTokenRef.current;
      setLoadingDoctors(true);
      try {
        let list = [];
        if (isDoctor && !isOwner) {
          list = userDoctorId
            ? [{ id: userDoctorId, clinic_id: userClinicId }]
            : [];
        } else if ((isAdmin || isOwner || isFrontdesk) && targetClinicId) {
          // Use centralized service with dedupe
          list = await fetchDoctorsByClinic(targetClinicId, { force: false });
        } else {
          // Admin with no clinic or aggregate mode
          list = await fetchAllDoctors({ force: false });
        }
        if (token === doctorsReqTokenRef.current) {
          setDoctors(list);
          // Adjust doctorId if invalid
          if (
            doctorId &&
            !list.some(
              (d) => String(d.id || d._id || d.doctor_id) === String(doctorId),
            )
          ) {
            if (isDoctor) setDoctorId(String(userDoctorId));
            else setDoctorId("");
          }
        }
      } catch (e) {
        if (token === doctorsReqTokenRef.current) {
          setDoctors([]);
          if (!isDoctor) setDoctorId("");
        }
      } finally {
        if (token === doctorsReqTokenRef.current) setLoadingDoctors(false);
      }
    },
    [
      doctorId,
      isAdmin,
      isOwner,
      isFrontdesk,
      isDoctor,
      userDoctorId,
      userClinicId,
    ],
  );

  // Load doctors initially and whenever clinic changes
  useEffect(() => {
    loadDoctors(clinicId);
  }, [clinicId, loadDoctors]);

  // Keep date range valid
  useEffect(() => {
    if (fromDate > toDate) setToDate(fromDate);
  }, [fromDate, toDate]);

  const forceReloadDoctors = useCallback(
    () => loadDoctors(clinicId),
    [loadDoctors, clinicId],
  );

  const doctorIdsEffective =
    isDoctor && !isOwner
      ? userDoctorId
        ? [String(userDoctorId)]
        : []
      : doctorId === "" || doctorId === null
        ? doctors.map((d) => String(d.id || d._id || d.doctor_id))
        : [String(doctorId)];

  const contextValue = {
    filters: {
      clinicId,
      // keep the raw `doctorId` state here so consumers can distinguish empty-string aggregate ('') from null
      doctorId,
      doctorIdsEffective,
      fromDate,
      toDate,
      roleContext: roleMeta,
    },
    loading: { clinics: loadingClinics, doctors: loadingDoctors },
    lists: { clinics, doctors },
    actions: {
      setClinicId: (val) => setClinicId(val || null),
      // Normalize doctor id inputs to strings but allow '' to represent 'all/aggregate' explicitly.
      // Accept null -> '' so callers that pass null clear to aggregate mode.
      setDoctorId: (val) => setDoctorId(val === null ? "" : String(val || "")),
      setFromDate,
      setToDate,
      forceReloadDoctors,
    },
  };

  return (
    <GlobalFiltersContext.Provider value={contextValue}>
      {children}
    </GlobalFiltersContext.Provider>
  );
}
